import * as React from "react"
import { useMutation } from "react-query"
import { FiEdit2, FiTrash } from "react-icons/fi"
import { Flex, Avatar, IconButton, Text, Badge, Select } from "@chakra-ui/react"
import { useMe } from "./providers/MeProvider"
import { fetcher } from "../lib/fetcher"
import { humanize } from "../lib/helpers/text"
import { UserEntity } from "../lib/types/user"
import { isAdmin } from "../lib/helpers/role"
import { useToast } from "../lib/hooks/useToast"
import { Affiliation } from "./UserModal"
import { ROLE_OPTIONS } from "../lib/static/user"
import { UserAffiliationConfirmOp } from "./UserAffiliationConfirmOp"

interface DeleteAffiliationResponse {
  organization: string
}

interface DeleteAffiliationRequest {
  affiliationId: string
}

interface EditAffiliationResponse {
  organization: string
  state: string
}

interface EditAffiliationRequest {
  affiliationId: string
  role: string
}

interface UserAffiliationControlsProps {
  affiliation: Affiliation
  affiliationsCount: number
  index: number
  user: UserEntity
  refetch: () => void
  onRefetch: () => void
}

export const UserAffiliationControls: React.FC<UserAffiliationControlsProps> = ({
  affiliation,
  affiliationsCount,
  index,
  user,
  refetch,
  onRefetch,
}) => {
  const me = useMe()
  const toast = useToast()
  const [newRole, setNewRole] = React.useState(affiliation.role)
  const [startEditingRole, setStartEditingRole] = React.useState(false)
  const [startDeleting, setStartDeleting] = React.useState(false)

  const { mutateAsync: deleteAffiliation, isLoading: deleteLoading } = useMutation<
    DeleteAffiliationResponse,
    unknown,
    DeleteAffiliationRequest
  >(({ affiliationId }) => fetcher(`affiliations/${affiliationId}/`, { method: "DELETE" }))

  const handleDeleteAffiliation = async (affiliationId: string) => {
    // even though button is disabled, function still gets called, have raised with Chakra
    // https://github.com/chakra-ui/chakra-ui/issues/1436
    if (!isAdmin(me.roles)) return
    if (isDisabled(affiliation)) return
    if (user.state !== "disabled" && affiliationsCount <= 1) {
      toast({
        title: "Refused",
        description: "Please disable user before removing last affiliation",
        status: "error",
      })
      return
    }
    try {
      const res = await deleteAffiliation({ affiliationId })
      if (!res) {
        refetch()
        onRefetch()
        toast({ description: "Successfully deleted affiliation" })
      } else {
        toast({ description: "Error deleting affiliation", status: "error" })
      }
    } catch (error) {
      toast({ title: "Network error", description: "Please check your internet connection", status: "error" })
    }
  }

  const { mutateAsync: editAffiliationRole, isLoading: editRoleLoading } = useMutation<
    EditAffiliationResponse,
    unknown,
    EditAffiliationRequest
  >(({ affiliationId, role }) =>
    fetcher(`affiliations/${affiliationId}/`, { body: JSON.stringify({ role }), method: "PATCH" }),
  )

  const handleEditAffiliationRole = async ({
    affiliationId,
    role,
  }: {
    affiliationId: string
    role: string
  }) => {
    if (!isAdmin(me.roles)) return
    if (isDisabled(affiliation)) return
    try {
      const res = await editAffiliationRole({ affiliationId, role })
      if (res.state === "new") {
        setStartEditingRole(false)
        refetch()
        onRefetch()
        toast({ description: "Successfully edited affiliation role" })
      } else {
        toast({ description: "Error editing affiliation role", status: "error" })
      }
    } catch (error) {
      toast({ title: "Network error", description: "Please check your internet connection", status: "error" })
    }
  }

  const isDisabled = (affiliation: Affiliation) => {
    if (!isAdmin(me.roles)) return true
    const url = affiliation.user.replace(/\/+$/, "")
    const userId = url.substring(url.lastIndexOf("/") + 1)
    return userId === me.uuid
  }

  return (
    <Flex justify="space-between" align="center" mt={index > 0 ? 2 : 0}>
      <Flex align="center">
        <Avatar
          name={affiliation.organization_name}
          src={affiliation.organization_icon}
          size="sm"
          bgColor="transparent"
          mr={4}
        />
        <Text>{affiliation.organization_name}</Text>
      </Flex>
      <Flex align="center" backgroundColor={startDeleting ? "gray.100" : "white"} px={1} borderRadius={5}>
        {!startDeleting && !startEditingRole && (
          <Badge colorScheme="cyan" fontSize=".7rem" mr={2} cursor="default" variant="subtle">
            {humanize(affiliation.role)}
          </Badge>
        )}

        {startDeleting && (
          <Text color="gray.600" fontSize="0.9rem" mr={2} fontWeight="bold">
            CONFIRM?
          </Text>
        )}

        {startEditingRole && (
          <Select size="xs" onChange={({ target }) => setNewRole(target.value)} value={newRole}>
            {ROLE_OPTIONS.map((role) => (
              <option key={role.value} value={role.value}>
                {role.label}
              </option>
            ))}
          </Select>
        )}

        {!startDeleting && !startEditingRole && (
          <>
            <IconButton
              icon={<FiEdit2 />}
              variant="ghost"
              aria-label="edit"
              color="gray.700"
              boxSize="30px"
              minW="30px"
              cursor="pointer"
              p={1}
              isDisabled={isDisabled(affiliation)}
              isLoading={editRoleLoading}
              onClick={() => {
                setStartEditingRole(true)
                setNewRole(affiliation.role)
              }}
            />
            <IconButton
              icon={<FiTrash />}
              variant="ghost"
              aria-label="delete"
              color="gray.700"
              boxSize="30px"
              minW="30px"
              cursor="pointer"
              p={1}
              isDisabled={isDisabled(affiliation)}
              isLoading={deleteLoading}
              onClick={() => setStartDeleting(true)}
            />
          </>
        )}

        {startDeleting && (
          <UserAffiliationConfirmOp
            isDisabled={isDisabled(affiliation)}
            isLoading={deleteLoading}
            onCancel={() => setStartDeleting(false)}
            onConfirm={() => handleDeleteAffiliation(affiliation.uuid)}
          />
        )}

        {startEditingRole && (
          <UserAffiliationConfirmOp
            isDisabled={isDisabled(affiliation)}
            isLoading={editRoleLoading}
            onCancel={() => setStartEditingRole(false)}
            onConfirm={() => handleEditAffiliationRole({ affiliationId: affiliation.uuid, role: newRole })}
          />
        )}
      </Flex>
    </Flex>
  )
}
